import { Component, OnInit, inject } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import {
  ICliente,
  IFacturacionCliente,
  IQueryParam,
  IResumenDatosBase,
} from 'modelos/src';
import { ClientesService } from '../../../auxiliares/servicios/http/clientes.service';
import { FacturacionClientesService } from '../../../auxiliares/servicios/http/facturacion-clientes.service';
import { ResumenesDatosService } from '../../../auxiliares/servicios/http/resumenes-datos.service';
import { HelperService } from '../../../auxiliares/servicios/helper.service';
import { DialogService } from '../../../auxiliares/dialog/dialog.service';
import {
  CrearEditarFacturacionComponent,
  IDataDialogFacturacion,
} from './crear-editar-facturacion/crear-editar-facturacion.component';
import { CrearResumenComponent } from './crear-resumen/crear-resumen.component';

@Component({
  selector: 'app-facturacion',
  templateUrl: './facturacion.component.html',
  styleUrl: './facturacion.component.scss',
  standalone: false,
})
export class FacturacionComponent implements OnInit {
  private clientesService = inject(ClientesService);
  private facturacionService = inject(FacturacionClientesService);
  private resumenesService = inject(ResumenesDatosService);
  private helper = inject(HelperService);
  private dialog = inject(MatDialog);
  private dialogService = inject(DialogService);

  public loading = true;
  /** Clientes facturables: nivel 1 + raíces independientes (con facturación propia). */
  public clientes: ICliente[] = [];
  /** Facturación creada por idCliente. */
  public facturacionPorCliente: Record<string, IFacturacionCliente> = {};
  /** Resumen del mes seleccionado por idCliente. */
  public resumenMesAnteriorPorCliente: Record<
    string,
    IResumenDatosBase<'Gastos del Cliente'>
  > = {};
  public loadingResumenes = false;
  /** Mes/año seleccionado en la pestaña Facturación (default: mes anterior). */
  public mesSel = 0;
  public anioSel = new Date().getFullYear();
  public aniosOpciones: number[] = [];
  public categorias: { key: 'trackers' | 'alarmas' | 'camaras'; label: string }[] =
    [
      { key: 'trackers', label: 'Trackers' },
      { key: 'alarmas', label: 'Alarmas' },
      { key: 'camaras', label: 'Cámaras' },
    ];
  private mesesNombres = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
  ];

  async ngOnInit(): Promise<void> {
    const ahora = new Date();
    const prev = new Date(ahora.getFullYear(), ahora.getMonth() - 1, 1);
    this.mesSel = prev.getMonth();
    this.anioSel = prev.getFullYear();
    const anio = ahora.getFullYear();
    this.aniosOpciones = [anio, anio - 1, anio - 2, anio - 3];
    await this.cargar();
  }

  /** Opciones de mes para el selector (0-based). */
  public get mesesOpciones(): { valor: number; nombre: string }[] {
    return this.mesesNombres.map((nombre, valor) => ({ valor, nombre }));
  }

  public get labelMesSel(): string {
    return `${this.mesesNombres[this.mesSel]} ${this.anioSel}`;
  }

  /** Clientes nivel 1 que ya tienen facturación creada. */
  public get conFacturacion(): number {
    return Object.keys(this.facturacionPorCliente).length;
  }

  private async cargar(): Promise<void> {
    this.loading = true;
    try {
      // 1. Clientes nivel 1 (subárbol del root).
      const queryNivel1: IQueryParam = {
        filter: JSON.stringify({ nivel: 1 }),
        select: '_id nombre nivel',
        includeChildren: true,
        childrenLevel: 0,
        sort: 'nombre',
        limit: 0,
      };
      // 2. Todas las facturaciones visibles (nivel 0 ve todo).
      const [listadoNivel1, listadoFact] = await Promise.all([
        this.clientesService.getFiltered(queryNivel1),
        this.facturacionService.getFiltered({
          select: '_id idCliente activa bonificado',
          limit: 0,
        }),
      ]);

      const nivel1 = listadoNivel1.datos ?? [];

      // Indexar facturación por idCliente.
      this.facturacionPorCliente = {};
      for (const f of listadoFact.datos ?? []) {
        if (f.idCliente) {
          this.facturacionPorCliente[f.idCliente] = f;
        }
      }

      // 3. Raíces independientes: clientes con facturación que no son nivel 1.
      const idsNivel1 = new Set(nivel1.map((c) => c._id));
      const idsIndependientes = Object.keys(this.facturacionPorCliente).filter(
        (id) => !idsNivel1.has(id),
      );
      let independientes: ICliente[] = [];
      if (idsIndependientes.length) {
        const queryIndep: IQueryParam = {
          filter: JSON.stringify({ _id: { $in: idsIndependientes } }),
          select: '_id nombre nivel',
          includeChildren: true,
          childrenLevel: 0,
          sort: 'nombre',
          limit: 0,
        };
        const listadoIndep = await this.clientesService.getFiltered(queryIndep);
        independientes = listadoIndep.datos ?? [];
      }

      this.clientes = [...nivel1, ...independientes];

      // 4. Resúmenes del mes seleccionado (para la pestaña Facturación).
      await this.cargarResumenesMes();
    } catch (error) {
      console.error('Error cargando facturación de clientes', error);
      this.helper.notifError('No se pudieron cargar los clientes');
    } finally {
      this.loading = false;
    }
  }

  public async onMesChange(): Promise<void> {
    await this.cargarResumenesMes();
  }

  /** Trae los resúmenes 'Gastos del Cliente' del mes seleccionado, por cliente. */
  private async cargarResumenesMes(): Promise<void> {
    this.loadingResumenes = true;
    try {
      const inicio = new Date(this.anioSel, this.mesSel, 1);
      const finMes = new Date(this.anioSel, this.mesSel + 1, 1);
      const query: IQueryParam = {
        filter: JSON.stringify({
          tipo: 'Gastos del Cliente',
          periodoInicio: {
            $gte: inicio.toISOString(),
            $lt: finMes.toISOString(),
          },
        }),
        select: 'resumen idCliente',
        includeChildren: true,
        childrenLevel: 0,
        limit: 0,
      };
      const listado =
        await this.resumenesService.getFiltered<'Gastos del Cliente'>(query);
      this.resumenMesAnteriorPorCliente = {};
      for (const r of listado.datos ?? []) {
        if (r.idCliente) {
          this.resumenMesAnteriorPorCliente[r.idCliente] = r;
        }
      }
    } catch (error) {
      console.error('Error cargando resúmenes del mes', error);
      this.helper.notifError('No se pudieron cargar los resúmenes');
    } finally {
      this.loadingResumenes = false;
    }
  }

  /** Elimina la facturación de un cliente (con confirmación). */
  public async eliminarFacturacion(cliente: ICliente): Promise<void> {
    const f = this.facturacionDe(cliente);
    if (!f?._id) {
      return;
    }
    const ok = await this.dialogService.confirm(
      'Eliminar facturación',
      `¿Eliminar la facturación de ${cliente.nombre}?`,
    );
    if (!ok) {
      return;
    }
    try {
      await this.facturacionService.delete(f._id);
      this.helper.notifSuccess('Facturación eliminada');
      await this.cargar();
    } catch (error) {
      console.error('Error eliminando facturación', error);
      this.helper.notifError(error);
    }
  }

  public resumenDe(
    cliente: ICliente,
  ): IResumenDatosBase<'Gastos del Cliente'> | undefined {
    return cliente._id
      ? this.resumenMesAnteriorPorCliente[cliente._id]
      : undefined;
  }

  public tieneFacturacion(cliente: ICliente): boolean {
    return !!cliente._id && !!this.facturacionPorCliente[cliente._id];
  }

  public facturacionDe(cliente: ICliente): IFacturacionCliente | undefined {
    return cliente._id ? this.facturacionPorCliente[cliente._id] : undefined;
  }

  public esBonificado(cliente: ICliente): boolean {
    return !!this.facturacionDe(cliente)?.bonificado;
  }

  /** Independiente: tiene facturación propia y no es nivel 1. */
  public esIndependiente(cliente: ICliente): boolean {
    return cliente.nivel !== 1 && this.tieneFacturacion(cliente);
  }

  public crearOEditar(cliente: ICliente): void {
    this.abrirDialog({ cliente, facturacion: this.facturacionDe(cliente) });
  }

  /** Crear facturación para cualquier cliente del árbol (excepción). */
  public agregarExcepcion(): void {
    this.abrirDialog({});
  }

  /** Abre el diálogo para generar el resumen mensual. */
  public crearResumen(): void {
    this.dialog.open(CrearResumenComponent, { autoFocus: false });
  }

  private abrirDialog(data: IDataDialogFacturacion): void {
    const config: MatDialogConfig<IDataDialogFacturacion> = {
      data,
      minWidth: '380px',
      autoFocus: false,
    };
    this.dialog
      .open(CrearEditarFacturacionComponent, config)
      .afterClosed()
      .subscribe(async (res) => {
        if (res) {
          await this.cargar();
        }
      });
  }
}
